import React, { useEffect, useState } from 'react';
import { MemberSidebarLayout } from '../SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Store, Clock, XCircle, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ReuploadIdDocsCard } from '../ReuploadIdDocsCard';
import { BusinessLogoCard } from './BusinessLogoCard';
import { BusinessPermitCard } from './BusinessPermitCard';

const BUSINESS_TYPES = {
  food: 'Food & Beverage', retail: 'Retail & Shopping', health: 'Health & Wellness',
  beauty: 'Beauty & Salon', tech: 'Technology & IT', travel: 'Travel & Tourism',
  education: 'Education & Training', entertainment: 'Entertainment', automotive: 'Automotive',
  real_estate: 'Real Estate', professional: 'Professional Services', other: 'Other',
};

/**
 * Editable resubmit form for merchants whose application is pending or
 * rejected. PUT /api/merchant/profile auto-flips rejected→pending on save.
 */
export function PendingOrRejectedView({ dashboard, status, info, onResubmitted, api }) {
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    name: dashboard?.name || '',
    mobile: dashboard?.mobile || '',
    business_name: info.business_name || '',
    business_type: info.business_type || '',
    industry_id: info.industry_id || '',
    business_address: info.business_address || '',
    business_description: info.business_description || '',
    business_logo_url: info.business_logo_url || '',
    business_permit_url: info.business_permit_url || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/industries').then(({ data }) => setIndustries(data.industries || data || []))
      .catch(() => {});
  }, [api]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.business_name.trim()) { toast.error('Business name is required'); return; }
    if (!form.business_address.trim()) { toast.error('Business address is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/api/merchant/profile', form);
      if (data.resubmitted) {
        toast.success('Application resubmitted — our team will review it shortly');
      } else {
        toast.success('Your application has been updated');
      }
      onResubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const isRejected = status === 'rejected';

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="merchant-dashboard">
        <div className={`p-4 rounded-xl border mb-6 ${
          isRejected ? 'bg-[#FF1744]/5 border-[#FF1744]/30' : 'bg-amber-500/5 border-amber-500/30'
        }`} data-testid="amend-status-banner">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isRejected ? 'bg-[#FF1744]/15' : 'bg-amber-500/15'
            }`}>
              {isRejected ? <XCircle className="w-5 h-5 text-[#FF1744]" /> : <Clock className="w-5 h-5 text-amber-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-outfit text-xl font-bold text-white">
                {isRejected ? 'Application Not Approved' : 'Application Under Review'}
              </h1>
              <p className="text-[#A0B4CC] text-sm mt-1">
                {isRejected
                  ? <>Your application was not approved. <b>You can amend your information below and resubmit for review.</b>{info.rejection_reason ? <span className="block text-[#FF1744] text-xs mt-1 font-medium">Reason: {info.rejection_reason}</span> : null}</>
                  : 'Your merchant application is being reviewed by our team. You can still update your information below — we\'ll use the latest version.'}
              </p>
            </div>
          </div>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Store className="w-4 h-4 text-[#00E5FF]" /> Business Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <BusinessLogoCard api={api} currentUrl={form.business_logo_url} businessName={form.business_name} onUpdated={() => { onResubmitted?.(); }} />

            <BusinessPermitCard api={api} currentUrl={form.business_permit_url} businessName={form.business_name}
              onUpdated={(url) => setForm(f => ({ ...f, business_permit_url: url || '' }))} />

            <div className="rounded-lg border border-white/10 bg-[#000B18]/40 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="text-white font-outfit font-semibold text-sm mb-1">Identity Verification</h4>
                  <p className="text-[#A0B4CC] text-xs">
                    Re-upload your government ID and a fresh selfie. We&apos;ll automatically re-run OCR to verify your identity.
                  </p>
                </div>
                <ReuploadIdDocsCard
                  api={api}
                  currentIdUrl={dashboard?.id_image_url}
                  currentSelfie={dashboard?.selfie_image_url}
                  onUpdated={() => onResubmitted?.()}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A0B4CC] text-xs">Owner Name *</Label>
                <Input value={form.name} onChange={onChange('name')} className="bg-[#000B18] border-white/20 text-white" data-testid="amend-name" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Mobile</Label>
                <Input value={form.mobile} onChange={onChange('mobile')} className="bg-[#000B18] border-white/20 text-white" data-testid="amend-mobile" />
              </div>
            </div>

            <div>
              <Label className="text-[#A0B4CC] text-xs">Business Name *</Label>
              <Input value={form.business_name} onChange={onChange('business_name')} className="bg-[#000B18] border-white/20 text-white" data-testid="amend-business-name" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A0B4CC] text-xs">Business Type</Label>
                <select value={form.business_type} onChange={onChange('business_type')}
                  className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="amend-business-type">
                  <option value="">Select type…</option>
                  {Object.entries(BUSINESS_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Industry</Label>
                <select value={form.industry_id} onChange={onChange('industry_id')}
                  className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="amend-industry">
                  <option value="">Select industry…</option>
                  {industries.map(ind => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-[#A0B4CC] text-xs">Business Address *</Label>
              <Input value={form.business_address} onChange={onChange('business_address')} className="bg-[#000B18] border-white/20 text-white" data-testid="amend-business-address" />
            </div>

            <div>
              <Label className="text-[#A0B4CC] text-xs">Business Description</Label>
              <textarea value={form.business_description} onChange={onChange('business_description')}
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
                data-testid="amend-business-description" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={submit} disabled={saving}
                className={`${isRejected ? 'bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90' : 'bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90'}`}
                data-testid="amend-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <><Save className="w-4 h-4 mr-1.5" />{isRejected ? 'Resubmit for Review' : 'Save changes'}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MemberSidebarLayout>
  );
}
